import { useState } from 'react';
import { t } from '@/lib/i18n/messages';
import { useJuthoorLands } from '@/hooks/use-juthoor-lands';
import { JuthoorLandPolygonMap } from './juthoor-land-polygon-map';
import type { Locale } from '@/types/auth';
import type { JuthoorLand, JuthoorRewardsInput } from '@/types/juthoor';

export interface WizardStepRewardsJuthoorProps {
  locale: Locale;
  initialData?: Partial<JuthoorRewardsInput>;
  onValidSubmit: (data: JuthoorRewardsInput) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

function minStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0] ?? '';
}

interface FormErrors {
  total_trees?: string;
  reward_per_tree?: string;
  max_trees_per_client?: string;
  netzero_land_id?: string;
  start_date?: string;
}

export function WizardStepRewardsJuthoor({
  locale,
  initialData,
  onValidSubmit,
  onBack,
  isSubmitting,
}: WizardStepRewardsJuthoorProps) {
  const isRTL = locale === 'ar';
  const { data: lands, loading: landsLoading } = useJuthoorLands();

  const [totalTrees, setTotalTrees] = useState(
    initialData?.total_trees ? String(initialData.total_trees) : ''
  );
  const [rewardPerTree, setRewardPerTree] = useState(
    initialData?.reward_per_tree ? String(initialData.reward_per_tree) : ''
  );
  const [maxTreesPerClient, setMaxTreesPerClient] = useState(
    initialData?.max_trees_per_client ? String(initialData.max_trees_per_client) : ''
  );
  const [selectedLand, setSelectedLand] = useState<JuthoorLand | null>(() => {
    if (!initialData?.netzero_land_id) return null;
    return null; // resolved once lands are loaded via onChange
  });
  const [selectedLandId, setSelectedLandId] = useState<string>(
    initialData?.netzero_land_id ? String(initialData.netzero_land_id) : ''
  );
  const [startDate, setStartDate] = useState(initialData?.start_date ?? '');
  const [budget, setBudget] = useState(
    initialData?.budget != null ? String(initialData.budget) : ''
  );
  const [errors, setErrors] = useState<FormErrors>({});

  function handleLandChange(landId: string) {
    setSelectedLandId(landId);
    const found = lands.find((l) => String(l.landId) === landId) ?? null;
    setSelectedLand(found);
    if (errors.netzero_land_id) setErrors((e) => ({ ...e, netzero_land_id: undefined }));
  }

  function validate(): boolean {
    const errs: FormErrors = {};

    const total = parseInt(totalTrees, 10);
    const reward = parseFloat(rewardPerTree);
    const maxPerClient = parseInt(maxTreesPerClient, 10);

    if (!total || total < 1) {
      errs.total_trees = isRTL ? 'يجب أن يكون العدد 1 على الأقل' : 'Must be at least 1';
    }
    if (!reward || reward <= 0) {
      errs.reward_per_tree = isRTL ? 'يجب أن تكون المكافأة أكبر من صفر' : 'Must be greater than 0';
    }
    if (!maxPerClient || maxPerClient < 1) {
      errs.max_trees_per_client = isRTL ? 'يجب أن يكون العدد 1 على الأقل' : 'Must be at least 1';
    } else if (!isNaN(total) && maxPerClient > total) {
      errs.max_trees_per_client = isRTL
        ? 'يجب ألا يتجاوز إجمالي الأشجار'
        : 'Cannot exceed total trees';
    }
    if (!selectedLandId) {
      errs.netzero_land_id = isRTL ? 'يرجى اختيار الأرض' : 'Please select a land';
    }
    if (!startDate) {
      errs.start_date = isRTL ? 'يرجى تحديد تاريخ البدء' : 'Please select a start date';
    } else if (startDate < minStartDate()) {
      errs.start_date = isRTL
        ? 'يجب أن يكون تاريخ البدء بعد 30 يومًا من اليوم'
        : 'Start date must be at least 30 days from today';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onValidSubmit({
      total_trees: parseInt(totalTrees, 10),
      reward_per_tree: parseFloat(rewardPerTree),
      max_trees_per_client: parseInt(maxTreesPerClient, 10),
      netzero_land_id: parseInt(selectedLandId, 10),
      land_name: selectedLand?.landName ?? '',
      land_polygon: selectedLand?.polygon ?? null,
      start_date: startDate,
      budget: budget ? parseFloat(budget) : null,
    });
  }

  const inputClass =
    'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const errorClass = 'mt-1 text-xs text-red-500';

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <h2 className="text-xl font-bold text-gray-800 dark:text-white">
        {t(locale, 'juthoorWizardStepTitle')}
      </h2>

      {/* Audience type — B2C (active) / B2B (coming soon) */}
      <div>
        <label className={labelClass}>
          {isRTL ? 'نوع الجمهور' : 'Audience'} *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AudienceTile
            active
            title={isRTL ? 'الأفراد · B2C' : 'B2C · Individuals'}
            sub={
              isRTL
                ? 'افتح المبادرة للأفراد المستفيدين.'
                : 'Open the initiative to individual sponsors.'
            }
          />
          <AudienceTile
            disabled
            title={isRTL ? 'الشركات · B2B' : 'B2B · Businesses'}
            sub={
              isRTL
                ? 'الشراكات مع الشركات قريباً.'
                : 'Corporate partnerships coming soon.'
            }
            badge={isRTL ? 'قريباً' : 'COMING SOON'}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Trees */}
        <div>
          <label className={labelClass}>{t(locale, 'totalTrees')} *</label>
          <input
            type="number"
            min="1"
            value={totalTrees}
            onChange={(e) => setTotalTrees(e.target.value)}
            className={inputClass}
            placeholder="100"
          />
          {errors.total_trees && <p className={errorClass}>{errors.total_trees}</p>}
        </div>

        {/* Reward per Tree */}
        <div>
          <label className={labelClass}>{t(locale, 'rewardPerTree')} (GP) *</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={rewardPerTree}
            onChange={(e) => setRewardPerTree(e.target.value)}
            className={inputClass}
            placeholder="10"
          />
          {errors.reward_per_tree && <p className={errorClass}>{errors.reward_per_tree}</p>}
        </div>

        {/* Max Trees per Client */}
        <div>
          <label className={labelClass}>{t(locale, 'maxTreesPerClient')} *</label>
          <input
            type="number"
            min="1"
            value={maxTreesPerClient}
            onChange={(e) => setMaxTreesPerClient(e.target.value)}
            className={inputClass}
            placeholder="5"
          />
          {errors.max_trees_per_client && (
            <p className={errorClass}>{errors.max_trees_per_client}</p>
          )}
        </div>

        {/* Start Date */}
        <div>
          <label className={labelClass}>{t(locale, 'startDate')} *</label>
          <input
            type="date"
            min={minStartDate()}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={inputClass}
          />
          {errors.start_date && <p className={errorClass}>{errors.start_date}</p>}
        </div>

        {/* Land Selector */}
        <div className="md:col-span-2">
          <label className={labelClass}>{t(locale, 'selectLand')} *</label>
          {landsLoading ? (
            <div className="h-10 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ) : (
            <select
              value={selectedLandId}
              onChange={(e) => handleLandChange(e.target.value)}
              className={inputClass}
            >
              <option value="">{t(locale, 'selectLand')}</option>
              {lands.map((land) => (
                <option key={land.landId} value={String(land.landId)}>
                  {land.landName}
                </option>
              ))}
            </select>
          )}
          {errors.netzero_land_id && (
            <p className={errorClass}>{errors.netzero_land_id}</p>
          )}
        </div>

        {/* Land Polygon Map Preview */}
        {selectedLand?.polygon?.points && selectedLand.polygon.points.length >= 3 && (
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <span className="material-icons-outlined text-base text-green-600">landscape</span>
              {selectedLand.landName}
            </p>
            <JuthoorLandPolygonMap
              points={selectedLand.polygon.points}
              landName={selectedLand.landName}
              height={260}
            />
          </div>
        )}

        {/* Budget (optional) */}
        <div className="md:col-span-2">
          <label className={labelClass}>
            {isRTL ? 'الميزانية (NC) - اختياري' : 'Budget (NC) — optional'}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className={inputClass}
            placeholder={isRTL ? 'اتركه فارغًا إذا لم يكن محددًا' : 'Leave blank if not set'}
          />
        </div>
      </div>

      {/* Actions */}
      <div className={`flex gap-3 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 py-3 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          {t(locale, 'back')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? t(locale, 'loading')
            : t(locale, 'submitForApproval')}
        </button>
      </div>
    </div>
  );
}

interface AudienceTileProps {
  title: string;
  sub: string;
  active?: boolean;
  disabled?: boolean;
  badge?: string;
}

function AudienceTile({ title, sub, active, disabled, badge }: AudienceTileProps) {
  return (
    <div
      aria-disabled={disabled || undefined}
      className={[
        'relative rounded-2xl border p-4 transition-all',
        active
          ? 'border-primary bg-primary/5 shadow-sm'
          : disabled
          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 opacity-70 cursor-not-allowed'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <p
            className={[
              'font-bold text-sm',
              active ? 'text-primary' : 'text-gray-800 dark:text-gray-100',
            ].join(' ')}
          >
            {title}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {sub}
          </p>
        </div>
        {active && (
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white"
          >
            <span className="material-icons-outlined text-[14px]">check</span>
          </span>
        )}
        {badge && (
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-sans text-[10px] tracking-widest uppercase">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}
